import React, { useEffect, useRef, useState } from "react";
import axios from "../../../api/axiosInstance";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { readStoredUser } from "../../../utils/sessionUser";
import PurchasingDocumentDialog from "./PurchasingDocumentDialog";
import "./PurchaseQuantityReview.css";

const number = (value) => value === "" || value == null ? null : Number(value);
export function purchaseQuantityReviewRequest({ documentType, documentId, date, currency = "EUR", lines = [] }) {
  return {
    document_type: documentType,
    document_id: number(documentId),
    date: String(date || "").slice(0, 10),
    ...(documentType === "delivery_note" ? { currency: String(currency || "EUR").trim().toUpperCase() } : {}),
    lines: lines.filter((line) => Number(line.item_id) > 0).map((line) => ({
      item_id: Number(line.item_id),
      unit_id: number(line.unit_id),
      ...(documentType === "requisition" ? {
        quantity: number(line.quantity),
        display_unit_level: line.display_unit_level || "unit",
        unit_price: number(line.unit_price),
        tender_currency: line.tender_currency || line.last_price_info?.tender_currency || null,
      } : {
        id: number(line.id), quantity_in_dn: number(line.quantity_in_dn),
        unit_level: line.unit_level || "unit", unit_factor: number(line.unit_factor),
        inventory_unit_id: number(line.inventory_unit_id), is_free_stock: Boolean(line.is_free_stock),
        unit_price_in_dn: number(line.unit_price_in_dn),
        price_status: line.price_status || null,
        price_estimate_source: line.price_estimate_source || null,
      }),
    })),
  };
}

const emptyReview = () => ({ key: "", warnings: [], token: null, acknowledged: false, open: false });

export function usePurchaseQuantityReview({ documentType, documentId, date, currency = "EUR", lines = [], enabled = true }) {
  const { languageId } = usePmsTheme();
  const spanish = String(languageId).startsWith("es");
  const delivery = documentType === "delivery_note";
  const user = readStoredUser();
  const input = { documentType, documentId, date, currency, lines };
  const request = purchaseQuantityReviewRequest(input);
  const scope = `${user?.id || ""}:${user?.active_business_unit_id || ""}`;
  const key = JSON.stringify([scope, request]);
  const latest = useRef(null);
  latest.current = { key, input, enabled, scope };
  const [state, setState] = useState(emptyReview);
  const stateRef = useRef(state);
  stateRef.current = state;
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { setState((previous) => enabled && previous.key === key ? previous : emptyReview()); }, [key, enabled]);
  const reset = () => setState(emptyReview());
  const show = (data, reviewKey) => setState({
    key: reviewKey, warnings: data.warnings, token: data.review_token,
    acknowledged: false, open: true,
  });
  const check = async (overrides = {}) => {
    const current = latest.current;
    if (!current.enabled) return { allowed: false };
    const payload = purchaseQuantityReviewRequest({ ...current.input, ...overrides });
    const reviewKey = JSON.stringify([current.scope, payload]);
    const previous = stateRef.current;
    if (previous.key === reviewKey && previous.warnings.length) {
      if (previous.acknowledged && previous.token && !previous.open) {
        return { allowed: true, token: previous.token, acknowledged: true };
      }
      setState({ ...previous, open: true });
      return { allowed: false };
    }
    try {
      const response = await axios.post("/api/purchasing/quantity-review/check", payload);
      if (!mounted.current || !latest.current.enabled || ![current.key, reviewKey].includes(latest.current.key)) return { allowed: false };
      const data = response?.data;
      if (!Array.isArray(data?.warnings) || (data.warnings.length && !data.review_token)) throw new Error("Invalid quantity review response");
      if (!data.warnings.length) { reset(); return { allowed: true, token: null, acknowledged: false }; }
      show(data, reviewKey);
      return { allowed: false };
    } catch (error) {
      if (!mounted.current || latest.current.key !== current.key) return { allowed: false };
      throw new Error(spanish
        ? "No se ha podido comprobar el historial de compras. Inténtalo de nuevo antes de continuar."
        : "The purchase history could not be checked. Please try again before continuing.");
    }
  };
  const handleError = (error) => {
    const data = error?.response?.data;
    if (data?.code !== "QUANTITY_REVIEW_REQUIRED" || !data.review_token || !Array.isArray(data.warnings) || !data.warnings.length) return false;
    show(data, latest.current.key);
    return true;
  };
  const visible = enabled && state.key === key && state.warnings.length > 0;
  const hasQuantityWarning = (warning) => warning.quantity_warning ?? !warning.price_warnings?.length;
  const quantityWarnings = state.warnings.some(hasQuantityWarning);
  const priceWarnings = state.warnings.some((warning) => warning.price_warnings?.length);
  const returnLabel = spanish ? (delivery ? "Volver al albarán" : "Volver a la solicitud") : (delivery ? "Return to delivery note" : "Return to requisition");
  const acknowledgement = priceWarnings ? (spanish
    ? (delivery
      ? "He revisado el albarán y he comprobado que las unidades de medida, las cantidades y los precios son correctos. Deseo continuar con la verificación del albarán."
      : "He revisado la solicitud de compra y he comprobado que las unidades de medida, las cantidades y los precios son correctos. Deseo continuar con la solicitud de compra.")
    : (delivery
      ? "I have reviewed the delivery note and checked that its units of measure, quantities and prices are correct. I wish to continue verifying the delivery note."
      : "I have reviewed the purchase requisition and checked that its units of measure, quantities and prices are correct. I wish to continue with the purchase requisition.")) : spanish
    ? (delivery
      ? "He revisado el albarán y he comprobado que las unidades de medida y las cantidades son correctas. Deseo continuar con la verificación del albarán."
      : "He revisado la solicitud de compra y he comprobado que las unidades de medida y las cantidades son correctas. Deseo continuar con la solicitud de compra.")
    : (delivery
      ? "I have reviewed the delivery note and checked that its units of measure and quantities are correct. I wish to continue verifying the delivery note."
      : "I have reviewed the purchase requisition and checked that its units of measure and quantities are correct. I wish to continue with the purchase requisition.");
  const format = (value) => Number(value || 0).toLocaleString(spanish ? "es-ES" : "en-GB", { maximumFractionDigits: 4 });
  const formatPrice = (value) => Number(value).toLocaleString(spanish ? "es-ES" : "en-GB", { maximumFractionDigits: 8 });
  const baselineLabel = (price) => price.baseline_source === "last_estimated_purchase"
    ? (spanish ? "Última compra con precio estimado" : "Last purchase with an estimated price")
    : price.baseline_source === "last_confirmed_purchase"
      ? (spanish ? "Última compra con precio confirmado" : "Last purchase with a confirmed price")
      : (spanish ? "Precio medio histórico" : "Historical average price");
  const title = priceWarnings
    ? quantityWarnings
      ? (spanish ? "Revisar cantidades y precios de compra" : "Review purchase quantities and prices")
      : (spanish ? "Precio de compra fuera de lo habitual" : "Purchase price outside the usual range")
    : (spanish ? "Cantidad de compra elevada" : "High purchase quantity");
  const warningDetails = <div className="purchase-quantity-review__details">
    {quantityWarnings && <p>{spanish
      ? "La cantidad indicada es al menos un 50 % superior al promedio de compra por albarán de los últimos 3 meses. Revisa las cantidades y las unidades de medida antes de continuar."
      : "The entered quantity is at least 50% above the average purchase per delivery note over the last 3 months. Review the quantities and units of measure before continuing."}</p>}
    {priceWarnings && <p>{spanish
      ? "Hay precios al menos un 20 % por encima o por debajo de la referencia de compras anteriores, comparados en la misma unidad y moneda. Revisa las unidades de medida aplicadas (cajas, packs o unidades individuales)."
      : "Some prices are at least 20% above or below the reference from previous purchases, compared in the same unit and currency. Review the applied units of measure (boxes, packs or individual units)."}</p>}
    <ul>{state.warnings.map((warning) => <li key={warning.item_id}>
      <strong>{warning.item_name || `#${warning.item_id}`}</strong>
      {hasQuantityWarning(warning) && <>
      <span>{spanish ? "Cantidad actual" : "Current quantity"}: {format(warning.requested_quantity)} {warning.unit_name_translations?.[languageId] || warning.unit_name}</span>
      <span>{spanish ? "Promedio por albarán" : "Average per delivery note"}: {format(warning.average_quantity)} {warning.unit_name_translations?.[languageId] || warning.unit_name}</span>
      <small>{spanish ? "Albaranes anteriores" : "Previous delivery notes"}: {warning.delivery_note_count}</small>
      </>}
      {(warning.price_warnings || []).map((price, index) => <div className="purchase-quantity-review__price" key={`${price.line_index}-${index}`}>
        <strong>{price.direction === "low" ? (spanish ? "Precio inferior" : "Lower price") : (spanish ? "Precio superior" : "Higher price")}: {price.direction === "low" ? "−" : "+"}{format(Math.abs((price.ratio - 1) * 100))} %
          {Number.isInteger(price.line_index) && <> · {spanish ? "Línea" : "Line"} {price.line_index + 1}</>}
        </strong>
        <span>{spanish ? "Precio unitario actual" : "Current unit price"}: {formatPrice(price.requested_unit_price)} {price.currency} / {warning.unit_name_translations?.[languageId] || warning.unit_name}</span>
        <span>{baselineLabel(price)}: {formatPrice(price.average_unit_price)} {price.currency} / {warning.unit_name_translations?.[languageId] || warning.unit_name}</span>
        {price.baseline_estimated && <strong>{spanish
          ? "Referencia provisional: el precio anterior también era estimado. Compruébalo con el documento del proveedor."
          : "Provisional reference: the previous price was also estimated. Check it against the supplier's document."}</strong>}
        {price.baseline_date && <small>{spanish ? "Fecha de la compra de referencia" : "Reference purchase date"}: {price.baseline_date}</small>}
        <small>{price.baseline_estimated
          ? (spanish ? "Albaranes de referencia con precio estimado" : "Reference delivery notes with estimated prices")
          : (spanish ? "Albaranes anteriores con precio confirmado" : "Previous delivery notes with confirmed prices")}: {price.delivery_note_count}
          {price.price_status === "estimated" && <> · {spanish ? "Precio actual estimado" : "Current price is estimated"}</>}
        </small>
      </div>)}
    </li>)}</ul>
    <p>{spanish
      ? "Vuelve al documento. Después de revisarlo, marca la casilla de comprobación para poder continuar."
      : "Return to the document. After reviewing it, select the review checkbox before continuing."}</p>
  </div>;
  return {
    check, handleError, reset,
    review: visible ? <>
      {!state.open && <aside className="purchase-quantity-review" aria-label={priceWarnings ? (spanish ? "Revisión de cantidades y precios" : "Quantity and price review") : (spanish ? "Revisión de cantidades" : "Quantity review")}>
        <label><input type="checkbox" checked={state.acknowledged}
          onChange={(event) => setState((previous) => ({ ...previous, acknowledged: event.target.checked }))} />
          <span>{acknowledgement}</span></label>
      </aside>}
      <PurchasingDocumentDialog isOpen={state.open} title={title}
        message={warningDetails} showCancel={false} confirmLabel={returnLabel} tone="warning"
        onConfirm={() => setState((previous) => ({ ...previous, open: false }))}
        onClose={() => setState((previous) => ({ ...previous, open: false }))} />
    </> : null,
  };
}
